import { useState, useEffect, useRef } from "react";
import "./css/adminorder.css";
import {
  getOrderList,
  getOrderDetail,
  searchOrders,
  processOrderCancel,
} from "../../utils/adminOrderService";
import { downloadOrderExcel } from "../../utils/adminExcelService";
import { formatDateTime } from "../../utils/commonService";
import Pagination from "../common/Pagination";

const AdminOrder = () => {
  const [orders, setOrders] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderCode, setSelectedOrderCode] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [listTotalPrice, setListTotalPrice] = useState(0);
  const [listCancelPrice, setListCancelPrice] = useState(0);
  const [listFinalPrice, setListFinalPrice] = useState(0);
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [selectedOrderDetailIdxList, setSelectedOrderDetailIdxList] = useState([]);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [showFloatingPagination, setShowFloatingPagination] = useState(false);
  const pageSize = 20;

  const getTodayDate = () => new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(getTodayDate());
  const [filterOrderState, setFilterOrderState] = useState("");
  const [filterCreditType, setFilterCreditType] = useState("");
  const [searchType, setSearchType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchParamsRef = useRef(null);

  const bottomPaginationRef = useRef(null);

  const orderStateToLabel = {
    PAYMENT_COMPLETED: "결제 완료",
    CANCELED: "전체 취소",
    PARTIAL_CANCELED: "부분 취소",
  };

  const creditTypeToLabel = {
    CARD: "신용/체크카드",
    VIRTUAL_ACC: "가상계좌",
    EASY_PAY: "간편결제",
    BANK_TRANSFER: "실시간 계좌이체",
    MOBILE: "휴대폰결제",
    PAYMENT_PENDING: "결제 대기 중",
  };

  // Floating Pagination 제어
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFloatingPagination(!entry.isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -60px 0px",
      },
    );

    if (bottomPaginationRef.current) {
      observer.observe(bottomPaginationRef.current);
    }

    return () => observer.disconnect();
  }, [totalPages]);

  useEffect(() => {
    if (searchParamsRef.current) {
      loadSearchResults(searchParamsRef.current, currentPage);
    } else {
      loadOrders(currentPage);
    }
  }, [currentPage]);

  const loadOrders = async (page) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getOrderList(page, pageSize);
      if (result.success) {
        setOrders(result.data.content || []);
        setTotalPages(result.data.totalPages || 0);
        setTotalElements(result.data.totalElements || 0);
        setListTotalPrice(result.data.totalPrice || 0);
        setListCancelPrice(result.data.totalCancelPrice || 0);
        setListFinalPrice(result.data.totalFinalPrice || 0);
      } else {
        setError(result.error);
        setOrders([]);
      }
    } catch (err) {
      setError("매출 목록을 불러오는데 실패했습니다.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSearchResults = async (params, page) => {
    setLoading(true);
    setError(null);
    try {
      const result = await searchOrders(params, page, pageSize);
      if (result.success) {
        setOrders(result.data.content || []);
        setTotalPages(result.data.totalPages || 0);
        setTotalElements(result.data.totalElements || 0);
        setListTotalPrice(result.data.totalPrice || 0);
        setListCancelPrice(result.data.totalCancelPrice || 0);
        setListFinalPrice(result.data.totalFinalPrice || 0);
      } else {
        setError(result.error);
        setOrders([]);
      }
    } catch (err) {
      setError("검색 결과를 불러오는데 실패했습니다.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const buildFilterParams = () => ({
    startDate: startDate || null,
    endDate: endDate || null,
    orderState: filterOrderState || null,
    creditType: filterCreditType || null,
    searchType: searchType || null,
    searchTerm: searchTerm || null,
  });

  const buildExcelParams = () => ({
    startDate: startDate || null,
    endDate: endDate || null,
    orderState: filterOrderState ? orderStateToLabel[filterOrderState] : null,
    creditType: filterCreditType ? creditTypeToLabel[filterCreditType] : null,
    searchType: searchType || null,
    searchTerm: searchTerm || null,
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = () => {
    const params = buildFilterParams();
    searchParamsRef.current = params;
    if (currentPage === 0) {
      loadSearchResults(params, 0);
    } else {
      setCurrentPage(0);
    }
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate(getTodayDate());
    setFilterOrderState("");
    setFilterCreditType("");
    setSearchType("");
    setSearchTerm("");
    searchParamsRef.current = null;
    if (currentPage === 0) {
      loadOrders(0);
    } else {
      setCurrentPage(0);
    }
  };

  const handleExcelDownload = async () => {
    setExcelLoading(true);
    const result = await downloadOrderExcel(buildExcelParams());
    if (!result.success) {
      alert(result.error || "엑셀 다운로드에 실패했습니다.");
    }
    setExcelLoading(false);
  };

  // 취소 선택 모드를 종료하고 선택된 orderDetailIdx 목록을 초기화
  const resetCancelSelection = () => {
    setShowCheckbox(false);
    setSelectedOrderDetailIdxList([]);
  };

  const handleCardClick = async (order) => {
    setSelectedOrderCode(order.orderCode);
    resetCancelSelection();
    setDetailLoading(true);
    setDetailError(null);
    setSelectedOrder(null);
    try {
      const result = await getOrderDetail(order.orderCode);
      if (result.success) {
        setSelectedOrder(result.data);
      } else {
        setDetailError(result.error);
      }
    } catch (err) {
      setDetailError("상세 정보를 불러오는데 실패했습니다.");
    } finally {
      setDetailLoading(false);
    }
  };

  // 상품 객체에서 취소 API에 사용할 orderDetailIdx를 추출
  const getOrderDetailIdx = (product) => product.orderDetailIdx;

  // 취소 승인된 상품 여부 (isRequest false + cancelResponseTime 존재)
  const isCanceledProduct = (p) => !p.isRequest && !!p.cancelResponseTime;

  // 현재 선택된 주문의 상세 정보를 API로 다시 조회해 우측 패널을 최신 상태로 갱신
  const refreshSelectedOrderDetail = async () => {
    if (!selectedOrderCode) return;
    const result = await getOrderDetail(selectedOrderCode);
    if (result.success) {
      setSelectedOrder(result.data);
    }
  };

  // 검색 여부에 따라 좌측 주문 목록을 API로 다시 조회해 최신 상태로 갱신
  const refreshOrderList = async () => {
    if (searchParamsRef.current) {
      await loadSearchResults(searchParamsRef.current, currentPage);
    } else {
      await loadOrders(currentPage);
    }
  };

  // 개별 상품 체크박스 선택/해제 시 selectedOrderDetailIdxList 상태를 토글
  const handleOrderDetailCheckboxChange = (orderDetailIdx) => {
    setSelectedOrderDetailIdxList((prev) =>
      prev.includes(orderDetailIdx)
        ? prev.filter((idx) => idx !== orderDetailIdx)
        : [...prev, orderDetailIdx],
    );
  };

  // 헤더 체크박스로 정상 주문 상품 전체 선택 또는 전체 해제
  const handleSelectAllOrderDetails = (e) => {
    if (e.target.checked) {
      setSelectedOrderDetailIdxList(
        normalProducts
          .map((product) => getOrderDetailIdx(product))
          .filter((idx) => idx != null),
      );
    } else {
      setSelectedOrderDetailIdxList([]);
    }
  };

  // 취소 선택 모드(체크박스 표시)를 켜거나 끄고 선택 목록을 초기화
  const handleToggleCancelMode = () => {
    setShowCheckbox((prev) => !prev);
    setSelectedOrderDetailIdxList([]);
  };

  // 선택된 상품에 대해 취소 API를 호출하고 성공 시 상세·목록을 새로고침
  const handleCancelProcess = async () => {
    if (selectedOrderDetailIdxList.length === 0) {
      alert("취소할 상품을 선택해주세요.");
      return;
    }

    if (
      !window.confirm(
        `선택한 ${selectedOrderDetailIdxList.length}개 상품을 취소 처리하시겠습니까?`,
      )
    ) {
      return;
    }

    setCancelLoading(true);
    try {
      const result = await processOrderCancel(selectedOrderDetailIdxList);
      if (result.success) {
        alert(result.data);
        resetCancelSelection();
        await refreshSelectedOrderDetail();
        await refreshOrderList();
      } else {
        alert(result.error);
      }
    } catch (err) {
      alert("취소 처리 중 오류가 발생했습니다.");
    } finally {
      setCancelLoading(false);
    }
  };

  const normalProducts = selectedOrder?.productList
    ? selectedOrder.productList.filter(
        (p) => !isCanceledProduct(p),
      )
    : [];
  const canceledProducts = selectedOrder?.productList
    ? selectedOrder.productList.filter(isCanceledProduct)
    : [];

  return (
    <div className="adminorder-page">
      <div className="adminorder-wrap">
        <div className="adminorder-filter">
          <div className="adminorder-filter-left">
            <div className="adminorder-filter-group">
              <div>조회 기간</div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span>~</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div className="adminorder-filter-divider" />

            <div className="adminorder-filter-group">
              <div>주문상태</div>
              <select
                value={filterOrderState}
                onChange={(e) => setFilterOrderState(e.target.value)}
              >
                <option value="">전체</option>
                <option value="PAYMENT_COMPLETED">결제 완료</option>
                <option value="CANCELED">전체 취소</option>
                <option value="PARTIAL_CANCELED">부분 취소</option>
              </select>
            </div>

            <div className="adminorder-filter-divider" />

            <div className="adminorder-filter-group">
              <div>결제수단</div>
              <select
                value={filterCreditType}
                onChange={(e) => setFilterCreditType(e.target.value)}
              >
                <option value="">전체</option>
                <option value="CARD">신용카드/체크카드</option>
                <option value="VIRTUAL_ACC">가상계좌</option>
                <option value="EASY_PAY">간편결제</option>
                <option value="BANK_TRANSFER">실시간 계좌이체</option>
                <option value="MOBILE">휴대폰결제</option>
                <option value="PAYMENT_PENDING">결제 대기 중</option>
              </select>
            </div>
          </div>

          <div className="adminorder-filter-right">
            <div className="adminorder-filter-group adminorder-filter-search">
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
              >
                <option value="">검색 유형</option>
                <option value="orderCode">주문코드</option>
                <option value="custNm">고객명</option>
                <option value="productNm">상품명</option>
              </select>
              <input
                type="text"
                placeholder="검색어를 입력해주세요"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="adminorder-search-btn" onClick={handleSearch}>
              조회
            </button>
            <button className="adminorder-reset-btn" onClick={handleReset}>
              초기화
            </button>
          </div>
        </div>

        <div className="adminorder-toolbar">
          <div>
            총 <span className="adminorder-toolbar-count">{totalElements}</span>{" "}
            건
          </div>
          <div className="adminorder-toolbar-summary">
            <div className="adminorder-toolbar-summary-item">
              <div className="adminorder-toolbar-summary-label">주문 금액</div>
              <div className="adminorder-toolbar-summary-value">
                {listTotalPrice.toLocaleString()}원
              </div>
            </div>
            <div className="adminorder-toolbar-summary-divider" />
            <div className="adminorder-toolbar-summary-item adminorder-toolbar-summary-item-cancel">
              <div className="adminorder-toolbar-summary-label">취소</div>
              <div className="adminorder-toolbar-summary-value">
                {listCancelPrice.toLocaleString()}원
              </div>
            </div>
            <div className="adminorder-toolbar-summary-divider" />
            <div className="adminorder-toolbar-summary-item adminorder-toolbar-summary-item-final">
              <div className="adminorder-toolbar-summary-label">최종 매출</div>
              <div className="adminorder-toolbar-summary-value">
                {listFinalPrice.toLocaleString()}원
              </div>
            </div>
          </div>
          <button onClick={handleExcelDownload} disabled={excelLoading}>
            {excelLoading ? "다운로드 중..." : "엑셀 다운로드"}
          </button>
        </div>

        <div className="adminorder-content">
          <div className="adminorder-list">
            {loading && (
              <div className="adminorder-detail-placeholder">
                불러오는 중...
              </div>
            )}
            {error && (
              <div className="adminorder-detail-placeholder">{error}</div>
            )}
            {!loading && !error && orders.length === 0 && (
              <div className="adminorder-detail-placeholder">
                주문 내역이 없습니다.
              </div>
            )}
            {!loading &&
              !error &&
              orders.map((order) => (
                <div
                  className={`adminorder-card${selectedOrderCode === order.orderCode ? " adminorder-card-active" : ""}`}
                  key={order.orderCode}
                  onClick={() => handleCardClick(order)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="adminorder-card-header">
                    <div className="adminorder-card-header-top">
                      <div>{order.orderCode}</div>
                      {order.orderState === "결제 완료" ? (
                        <div className="adminorder-card-state-complete">
                          {order.orderState}
                        </div>
                      ) : (
                        <div className="adminorder-card-state-cancel">
                          {order.orderState}
                        </div>
                      )}
                    </div>

                    <div className="adminorder-card-header-date">
                      <div>{formatDateTime(order.orderDateTime)}</div>
                    </div>
                  </div>

                  <div className="adminorder-card-body">
                    <div className="adminorder-card-body-info">
                      <div className="adminorder-card-customer">
                        <div>{order.custNm}</div>
                      </div>

                      <div className="adminorder-card-payment-row">
                        <div className="adminorder-card-payment">
                          <div>{order.creditType}</div>
                        </div>
                      </div>
                    </div>
                    <div className="adminorder-card-product-preview">
                      <div className="adminorder-card-product-name">
                        {order.productName}
                      </div>
                      <span className="adminorder-card-product-separator">
                        /
                      </span>
                      <div className="adminorder-card-product-price">
                        {order.totalPrice.toLocaleString()}원
                      </div>
                    </div>
                  </div>
                </div>
              ))}

            {/* Bottom Pagination */}
            <div ref={bottomPaginationRef}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>

            {/* Floating Pagination (리스트 내부에서 떠다니도록) */}
            {showFloatingPagination && totalPages > 1 && (
              <div className="adminorder-floating-pagination">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>

          <div className="adminorder-detail">
            {/* detail 부분은 기존 그대로 유지 */}
            {!selectedOrderCode ? (
              <div className="adminorder-detail-placeholder">
                주문을 선택해주세요.
              </div>
            ) : detailLoading ? (
              <div className="adminorder-detail-placeholder">
                불러오는 중...
              </div>
            ) : detailError ? (
              <div className="adminorder-detail-placeholder">{detailError}</div>
            ) : !selectedOrder?.productList?.length ? (
              <div className="adminorder-detail-placeholder">
                상품 정보가 없습니다.
              </div>
            ) : (
              <>
                <div className="adminorder-products">
                  <div className="adminorder-products-section">
                    <div className="adminorder-products-section-header">
                      <div className="adminorder-products-section-header-left">
                        <div>정상 주문</div>
                        <div className="adminorder-products-count adminorder-products-count-normal">
                          ({normalProducts.length}건)
                        </div>
                      </div>
                      {normalProducts.length > 0 && (
                        <div className="adminorder-products-section-actions">
                          {showCheckbox && (
                            <button
                              type="button"
                              className="adminorder-btn-cancel-confirm"
                              onClick={handleCancelProcess}
                              disabled={cancelLoading}
                            >
                              {cancelLoading ? "처리 중..." : "선택 처리"}
                            </button>
                          )}
                          <button
                            type="button"
                            className="adminorder-btn-cancel-mode"
                            onClick={handleToggleCancelMode}
                          >
                            {showCheckbox ? "취소" : "취소"}
                          </button>
                        </div>
                      )}
                    </div>
                    <table className="adminorder-product-table">
                      <colgroup>
                        {showCheckbox && <col style={{ width: "40px" }} />}
                        <col style={{ width: showCheckbox ? "200px" : "220px" }} />
                        <col style={{ width: showCheckbox ? "130px" : "140px" }} />
                        <col style={{ width: showCheckbox ? "130px" : "140px" }} />
                        <col style={{ width: showCheckbox ? "130px" : "140px" }} />
                      </colgroup>
                      <thead>
                        <tr>
                          {showCheckbox && (
                            <th className="adminorder-product-th-checkbox">
                              <input
                                type="checkbox"
                                onChange={handleSelectAllOrderDetails}
                                checked={
                                  selectedOrderDetailIdxList.length ===
                                    normalProducts.length &&
                                  normalProducts.length > 0
                                }
                              />
                            </th>
                          )}
                          <th>상품명</th>
                          <th>수량</th>
                          <th>단가</th>
                          <th>총금액</th>
                        </tr>
                      </thead>
                      <tbody>
                        {normalProducts.length > 0 ? (
                          normalProducts.map((product) => {
                            const orderDetailIdx = getOrderDetailIdx(product);
                            return (
                              <tr
                                key={orderDetailIdx}
                                className={
                                  showCheckbox &&
                                  selectedOrderDetailIdxList.includes(orderDetailIdx)
                                    ? "adminorder-product-row-selected"
                                    : ""
                                }
                              >
                                {showCheckbox && (
                                  <td className="adminorder-product-td-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={selectedOrderDetailIdxList.includes(
                                        orderDetailIdx,
                                      )}
                                      onChange={() =>
                                        handleOrderDetailCheckboxChange(
                                          orderDetailIdx,
                                        )
                                      }
                                    />
                                  </td>
                                )}
                                <td>
                                  <div className="adminorder-product-name">
                                    {product.productNm}
                                  </div>
                                  <div className="adminorder-product-store">
                                    {product.storeNm}
                                  </div>
                                </td>
                                <td>{product.orderDetailAmount}개</td>
                                <td>
                                  {product.orderDetailPrice.toLocaleString()}원
                                </td>
                                <td className="adminorder-product-total">
                                  {product.orderDetailTotal.toLocaleString()}원
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan={showCheckbox ? 5 : 4}
                              className="adminorder-product-table-empty"
                            >
                              해당하는 주문 건이 없습니다.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="adminorder-products-section">
                    <div className="adminorder-products-section-header adminorder-products-section-header-cancel">
                      <div>취소</div>
                      <div className="adminorder-products-count adminorder-products-count-cancel">
                        ({canceledProducts.length}건)
                      </div>
                    </div>
                    <table className="adminorder-product-table">
                      <colgroup>
                        <col style={{ width: "26%" }} />
                        <col style={{ width: "13%" }} />
                        <col style={{ width: "13%" }} />
                        <col style={{ width: "13%" }} />
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "17%" }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>상품명</th>
                          <th>수량</th>
                          <th>단가</th>
                          <th>총금액</th>
                          <th>취소일시</th>
                          <th>취소사유</th>
                        </tr>
                      </thead>
                      <tbody>
                        {canceledProducts.length > 0 ? (
                          canceledProducts.map((product, idx) => (
                            <tr key={idx}>
                              <td>
                                <div className="adminorder-product-name">
                                  {product.productNm}
                                </div>
                                <div className="adminorder-product-store">
                                  {product.storeNm}
                                </div>
                              </td>
                              <td>{product.orderDetailAmount}개</td>
                              <td>
                                {product.orderDetailPrice.toLocaleString()}원
                              </td>
                              <td className="adminorder-product-total">
                                {product.orderDetailTotal.toLocaleString()}원
                              </td>
                              <td className="adminorder-product-cancel-time">
                                {product.cancelResponseTime ? (
                                  <span>
                                    {formatDateTime(product.cancelResponseTime)}
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td className="adminorder-product-reason">
                                {product.requestReason ||
                                product.responseReason ? (
                                  <>
                                    {product.requestReason && (
                                      <span>{product.requestReason}</span>
                                    )}
                                    {product.requestReason &&
                                      product.responseReason && (
                                        <span className="adminorder-product-reason-sub">
                                          {" "}
                                          /{" "}
                                        </span>
                                      )}
                                    {product.responseReason && (
                                      <span className="adminorder-product-reason-sub">
                                        {product.responseReason}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="6"
                              className="adminorder-product-table-empty"
                            >
                              해당하는 주문 건이 없습니다.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="adminorder-summary">
                  <div className="adminorder-summary-item">
                    <span className="adminorder-summary-label">주문 금액</span>
                    <span className="adminorder-summary-value">
                      {selectedOrder.totalPrice.toLocaleString()}원
                    </span>
                  </div>
                  <span className="adminorder-summary-operator">−</span>
                  <div className="adminorder-summary-item adminorder-summary-item-cancel">
                    <span className="adminorder-summary-label">취소</span>
                    <span className="adminorder-summary-value">
                      {selectedOrder.cancelPrice.toLocaleString()}원
                    </span>
                  </div>
                  <span className="adminorder-summary-operator">=</span>
                  <div className="adminorder-summary-item adminorder-summary-item-final">
                    <span className="adminorder-summary-label">최종 매출</span>
                    <span className="adminorder-summary-value">
                      {selectedOrder.finalPrice.toLocaleString()}원
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrder;
